import { useTranslations } from '@aikoa/i18n/react'
import { type User } from '@supabase/supabase-js'
import { useCallback } from 'react'
import { View } from 'react-native'
import { UserAuthField } from '~/components/auth/UserAuthField'
import { useAlert } from '~/components/GlobalAlert'
import { Label } from '~/components/typography/Content'
import { useSession } from '~/components/user/SessionProvider'
import { queryClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'

export interface ContactMethodsChangeEvent {
  email?: string
  phoneNumber?: string
}

export function ContactMethodsManager({
  className,
  onChange,
  showEmail = true,
  showPhone = true,
}: {
  className?: string
  onChange?: (values: ContactMethodsChangeEvent) => void
  showEmail?: boolean
  showPhone?: boolean
}) {
  const { session } = useSession()
  const user = session?.user
  const { showAlert } = useAlert()
  const { t } = useTranslations('user')

  const isEmailPending = !!user?.new_email
  const isPhonePending = !!user?.new_phone

  const handleVerifyPhoneChange = useCallback(
    (verifiedUser: User | null) => {
      if (!verifiedUser) {
        showAlert({
          type: 'error',
          title: t(($) => $.contactMethods.error.title),
          description: t(($) => $.contactMethods.error.verificationFailed),
        })
        return
      }

      const phoneNumber = verifiedUser.phone ?? undefined

      if (phoneNumber) {
        showAlert({
          type: 'success',
          title: t(($) => $.contactMethods.success.title),
          description: t(($) => $.contactMethods.success.phoneUpdated),
        })

        onChange?.({ phoneNumber })
      }
    },
    [onChange, showAlert, t]
  )

  const handleOtpError = useCallback(
    (_error: unknown) => {
      showAlert({
        type: 'error',
        title: t(($) => $.contactMethods.error.title),
        description: t(($) => $.contactMethods.error.codeSendFailed),
      })
    },
    [showAlert, t]
  )

  const handleVerifyEmailChange = useCallback(
    (verifiedUser: User | null) => {
      if (!verifiedUser) {
        showAlert({
          type: 'error',
          title: t(($) => $.contactMethods.error.title),
          description: t(($) => $.contactMethods.error.verificationFailed),
        })
        return
      }

      const email = verifiedUser.email ?? undefined

      if (email) {
        showAlert({
          type: 'success',
          title: t(($) => $.contactMethods.success.title),
          description: t(($) => $.contactMethods.success.emailUpdated),
        })

        void queryClient.invalidateQueries({
          queryKey: ['userProfile'],
        })

        onChange?.({ email })
      }
    },
    [onChange, showAlert, t]
  )

  return (
    <View className={cn('gap-6', className)}>
      {showEmail && (
        <View className="gap-2">
          <Label>{t(($) => $.contactMethods.emailLabel)}</Label>
          <UserAuthField
            contactType="email"
            label={false}
            onVerify={handleVerifyEmailChange}
            onError={handleOtpError}
            className={cn(isEmailPending && 'mt-2')}
          />
        </View>
      )}

      {showPhone && (
        <View className="gap-2">
          <Label>{t(($) => $.contactMethods.phoneLabel)}</Label>
          <UserAuthField
            contactType="phone"
            label={false}
            onVerify={handleVerifyPhoneChange}
            onError={handleOtpError}
            className={cn(isPhonePending && 'mt-2')}
          />
        </View>
      )}
    </View>
  )
}
